import { Directive, EventEmitter, Input, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";

import { ApplicationContextService, ConfirmationModalComponent, Fb, IPaginationRequestBaseModel, IPaginationResponseModel, RolePermissionService, UtilitiesService } from "lib-core-shared";
import { BehaviorSubject, Observable, of, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { MESSAGES } from "../constants";
import { TabItem } from "../lib-tabs/models/tab-item-model";
import { PaginationModel } from "../models/pagination-model";
import { ComponentBaseDirective } from "./component.directive";

@Directive({
    selector: 'app-page-base'
})
export class PageBaseDirective<T> extends ComponentBaseDirective implements OnDestroy {
    @Input() public isNew: boolean = true;
    @Input() public tab!: TabItem;
    @Input() public id: string = '';
    public fb: Fb | undefined = { text: 'LDGILL', status: 'text-info' };
    public disableSave: boolean = true;
    @Input() broadCastSubmssion$ = new BehaviorSubject<boolean> (false);
    public submittedEvent: EventEmitter<boolean> = new EventEmitter<boolean> (false);
    public paginationDetails: IPaginationRequestBaseModel;
    public subscription: Subscription | undefined;
    public paginationDetails$ :BehaviorSubject<PaginationModel>;
    public totalTranslation: number = 0;
    public results:Observable<T[]>  | undefined;
    public result: Observable<T> | undefined;
    public messageLoadingRetrievalDescription: string = '';
    public messageLoadingSubmissionDescription: string = '';
    public messageErrorLoading: string = ';'
    @Input() resultReponse$: Observable<IPaginationResponseModel<T>> | undefined;
    @Input() response$: Observable<T> | undefined;
    @Input() delete$!: Observable<boolean>;
    @Input() save$!: Observable<T>;
    @Input() update$!: Observable<T>;
    public savedSuccessfullyMessage!: string;
    public errorSavingMessage!: string;
    public newId!: T;
    public resultUnboxed!: T[];
    constructor(
      public activatedRoute: ActivatedRoute,
      public router: Router,
      public modalService: NgbModal,
      public utilitiesService: UtilitiesService,
      public roleService: RolePermissionService,
      public applicationContext: ApplicationContextService
    ) {
      super(activatedRoute, router, roleService, applicationContext);
      this.paginationDetails = {
        currentpage: 1,
        rowsperpage: 10,
      }  as PaginationModel;
      this.paginationDetails$ = new BehaviorSubject<PaginationModel>(this.paginationDetails);
      this.utilitiesService.unSavedChangesConfirm = false;
  }
  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
  public ngbModalOption: NgbModalOptions = {
    backdrop: 'static',
    keyboard: false,
    size: 'lg'
  }
  public requestPageData(page: number) {
    if (this.utilitiesService.unSavedChangesConfirm) {
      const modalRef = this.modalService.open(ConfirmationModalComponent);
      modalRef.componentInstance.title = 'Confirmation';
      modalRef.componentInstance.body = MESSAGES.CONFIRMATION_UNSAVED_CHANGES;
      modalRef.componentInstance.confirmEmitter.subscribe((outcome: boolean) => {
        modalRef.close();
        if (outcome && this.paginationDetails) {
          this.paginationDetails.currentpage = page;
          this.getData();
        }
      });
    } else {
      if (this.paginationDetails) {
        this.paginationDetails.currentpage = page;
      }
      this.getData();
    }
  }

  public getDetail() {
    this.resetFeedBack();
    if (!this.silentLoading) {
      this.isBusy = true;
    }
    if (this.response$) {
      this.subscription =  this.response$.subscribe((response: T) => {
        this.result = of(response);
        this.isBusy = false;
        this.broadCastSubmssion$ = new BehaviorSubject<boolean> (true);
        this.submittedEvent.emit(true);
      }, e => { 
        this.isBusy = false;
        if (e.status !== 404) {
          this.reportError(e, this.messageErrorLoading);
        }
        this.submittedEvent.emit(false);
      });
    }
  }


  public getData() {
    this.resetFeedBack();
    if (!this.silentLoading) {
      this.isBusy = true;
    }

    //if we have a previous subscription active we need to unsubscribe from it
    //otherwise if the previous subscription/http request is resolved after the current request then the previous request data will be shown instead.
    //obiously not good for customer search results etc
    if(this.subscription){
      this.subscription.unsubscribe();
    }

    if (this.resultReponse$) {
      const that = this;
      this.subscription =  this.resultReponse$
      .pipe(
        //if no response data there's no point continuing
        filter((response: IPaginationResponseModel<T>) => {
          if(response && response.data){
            return true
          }else{
            this.broadCastSubmssion$.next(true);
            this.isBusy = false;
            return false
          }
        })
      )
      .subscribe((response: IPaginationResponseModel<T>) => {
        response.total = response.total ? response.total : 0;
        this.paginationDetails.totalPages = response.total ? Math.ceil(response.total / 10) : 0;
        this.totalTranslation = response?.total ? response?.total : 0;
        this.resultUnboxed = response?.data?.length > 100 ? response.data.slice(0, 100): response?.data ? response.data : [];
        that.results = response?.data?.length > 100 ? of(response.data.slice(0, 100)): response?.data ? of(response.data) : of([]);
        this.paginationDetails$.next(this.paginationDetails);
        this.broadCastSubmssion$.next(true);
        this.isBusy = false;
      }, e => { 
        this.isBusy = false;
        if (e.status !== 404) {
          this.reportError(e, this.messageErrorLoading);
        } else {
          that.results = of([]);
          this.totalTranslation = 0;
          this.broadCastSubmssion$.next(true);
          this.paginationDetails.totalPages = 0;
        }
      });
    }
  }

  public updateData() {
    this.resetFeedBack();
    this.isBusy = true;
    this.update$.subscribe(x =>
      {
        this.isBusy = false;
        this.utilitiesService.unSavedChangesConfirm = false;
        this.silentLoading = true;
        this.getData();
        // const modalRef = this.modalService.open(ModalComponent);
        // modalRef.componentInstance.title = 'Success';
        // modalRef.componentInstance.p = this.savedSuccessfullyMessage;
        // modalRef.result.then(() => {
          this.silentLoading = false;
          this.broadCastSubmssion$.next(true);
          this.closeEmitter.emit(true);
        // });
      } ,
      e => {
        this.broadCastSubmssion$.next(false);
        this.isBusy = false;
        this.silentLoading = false;
        if (e.status !== '404') {
          this.reportError(e, this.errorSavingMessage);
        }
      });
  }

  public deleteData() {
    this.resetFeedBack();
    this.isBusy = true;
    this.delete$.subscribe(x =>
      {
        this.isBusy = false;
        this.utilitiesService.unSavedChangesConfirm = false;
        this.silentLoading = true;
        this.getData();
        this.silentLoading = false;
        this.broadCastSubmssion$.next(true);
        this.closeEmitter.emit(true);
      } ,
      e => {
        this.isBusy = false;
        this.silentLoading = false;
        if (e.status !== 404) {
          if (e.triggerRefresh && e.message === 'Cannot delete addresses linked with a contact') {
            let title = e.message;
            e.message = e.messages.length > 1 ? e.messages.join('<br>') : e.message = e.messages;
            this.getData();
            this.broadCastSubmssion$.next(true);
            this.reportError(e, title);
            return
          }

          this.reportError(e, this.errorSavingMessage);
        }
        this.broadCastSubmssion$.next(false);
      });
  }

  public saveData() {
    this.resetFeedBack();
    this.isBusy = true;
    this.save$.subscribe(x =>
      {
        this.isBusy = false;
        this.newId = x;
        this.utilitiesService.unSavedChangesConfirm = false;
        this.silentLoading = true;
        this.getData();
        this.silentLoading = false;
        this.broadCastSubmssion$.next(true);
        this.closeEmitter.emit(true);
      } ,
      e => {
        this.isBusy = false;
        this.silentLoading = false;
        if (e.status !== 404) {
          this.reportError(e, this.errorSavingMessage);
        }
        this.broadCastSubmssion$.next(false);
      });
  }

  public closeModal() {
    this.modalService.dismissAll();
  }
}